import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { getAllInvoices, getSelectedInvoiceDetails } from '../../features/invoice/invoice.slice';
import { InvoicePreview } from '../invoice/invoice-preview.component';
import { InvoiceItem } from '../../features/invoice/invoice.service';
import './copy-recent-invoice-modal.style.scss';

interface Invoice {
    invoiceNumber: string;
    customerName: string;
    selected?: boolean;
}

interface CopyRecentInvoiceModalProps {
    show: boolean;
    onClose: () => void;
    onSelect: (invoice: Invoice, fullInvoiceData?: any) => void;
}

export const CopyRecentInvoiceModal: React.FC<CopyRecentInvoiceModalProps> = ({
    show,
    onClose,
    onSelect
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    
    // Redux state
    const { 
        allInvoices, 
        totalInvoicesCount, 
        invoicesLoading,
        selectedInvoiceDetails,
        selectedInvoiceLoading
    } = useAppSelector(
        (state: any) => state.invoice
    );

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedInvoiceData, setSelectedInvoiceData] = useState<InvoiceItem | null>(null);
    const [isLoadingCopy, setIsLoadingCopy] = useState(false);
    const pageSize = 10; // 10 items per page

    // Calculate total pages
    const totalPages = Math.ceil(totalInvoicesCount / pageSize);

    // Fetch invoices when modal opens or page/search changes
    useEffect(() => {
        if (show) {
            dispatch(getAllInvoices({
                PageNumber: currentPage,
                PageSize: pageSize,
                SearchQuery: searchQuery || undefined
            }));
        }
    }, [show, currentPage, searchQuery, dispatch]);

    // Reset to page 1 when search query changes
    useEffect(() => {
        if (searchQuery) {
            setCurrentPage(1);
        }
    }, [searchQuery]);

    const handleInvoiceClick = (invoice: any) => {
        setSelectedInvoice({
            invoiceNumber: `INV ${invoice.invoiceNumber}`,
            customerName: invoice.customer?.name || 'Unknown Customer'
        });
    };

    const handleSelectAndCopy = async () => {
        if (selectedInvoice) {
            // Extract just the invoice number without the "INV " prefix
            const invoiceNumber = selectedInvoice.invoiceNumber.replace('INV ', '');
            
            try {
                setIsLoadingCopy(true);
                // Dispatch the action to fetch selected invoice details
                const result = await dispatch(getSelectedInvoiceDetails(invoiceNumber)).unwrap();
                
                // Store in local state
                let fullInvoiceData = null;
                if (result && result.data && result.data.length > 0) {
                    fullInvoiceData = result.data[0];
                    setSelectedInvoiceData(fullInvoiceData);
                   }
                
                // Call the parent's onSelect handler with full invoice data
                onSelect(selectedInvoice, fullInvoiceData);
                onClose();
            } catch (error) {
                console.error('Failed to fetch invoice details:', error);
            } finally {
                setIsLoadingCopy(false);
            }
        }
    };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(prev => prev - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(prev => prev + 1);
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            size="lg"
            className="copy-recent-invoice-modal"
        >
            <Modal.Header className="border-0 pb-0">
                <div className="crm-title-wrap">
                    <span className="crm-title-icon" aria-hidden="true">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                    </span>
                    <Modal.Title className="modal-title-custom">
                        {t('COPY_RECENT_INVOICE') || 'Copy recent invoice'}
                    </Modal.Title>
                </div>
                <button
                    type="button"
                    className="btn-close"
                    onClick={onClose}
                    aria-label="Close"
                />
            </Modal.Header>

            <Modal.Body className="pt-2">
                <Row className="g-3">
                    {/* Left side - Invoice list */}
                    <Col md={6} className="invoice-list-column">
                        {/* Search bar */}
                        <div className="search-wrapper mb-3">
                            <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                className="search-icon"
                            >
                                <path
                                    d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                                    stroke="#6B7280"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                                <path
                                    d="M17.5 17.5L13.875 13.875"
                                    stroke="#6B7280"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <Form.Control
                                type="text"
                                placeholder={t('SEARCH_INVOICE_CLIENT') || 'Search invoice or client name...'}
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="search-input"
                            />
                        </div>

                        {/* Invoice list */}
                        <div className="invoice-list">
                            {invoicesLoading ? (
                                <div className="invoice-loader">
                                    <div className="loader-spinner">
                                        <div className="spinner"></div>
                                    </div>
                                    <p className="loader-text">{t('LOADING_INVOICES') || 'Loading invoices...'}</p>
                                </div>
                            ) : allInvoices.length === 0 ? (
                                <div className="empty-state">
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" className="empty-icon">
                                        <path d="M40 18L24 6L8 18V40C8 40.5304 8.21071 41.0391 8.58579 41.4142C8.96086 41.7893 9.46957 42 10 42H38C38.5304 42 39.0391 41.7893 39.4142 41.4142C39.7893 41.0391 40 40.5304 40 40V18Z" stroke="#9CA3AF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M18 42V24H30V42" stroke="#9CA3AF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <p className="empty-title">{t('NO_INVOICES_FOUND') || 'No invoices found'}</p>
                                    <p className="empty-subtitle">{searchQuery ? (t('TRY_DIFFERENT_SEARCH') || 'Try a different search term') : (t('NO_INVOICES_YET') || 'You haven\'t created any invoices yet')}</p>
                                </div>
                            ) : (
                                allInvoices.map((invoice: any) => (
                                    <div
                                        key={invoice.id}
                                        className={`invoice-item ${selectedInvoice?.invoiceNumber === `INV ${invoice.invoiceNumber}` ? 'selected' : ''}`}
                                        onClick={() => handleInvoiceClick(invoice)}
                                    >
                                        <span className="invoice-number">INV {invoice.invoiceNumber}</span>
                                        <div className="d-flex align-items-center">
                                            <span className="customer-name">{invoice.customer?.name || 'Unknown'}</span>
                                            <svg
                                                width="20"
                                                height="20"
                                                viewBox="0 0 20 20"
                                                fill="none"
                                                className="arrow-icon"
                                            >
                                                <path
                                                    d="M7.5 15L12.5 10L7.5 5"
                                                    stroke="#6B7280"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Pagination */}
                        <div className="pagination-wrapper mt-3">
                            <button
                                className="pagination-btn"
                                onClick={handlePrevPage}
                                disabled={currentPage === 1}
                            >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path
                                        d="M12.5 15L7.5 10L12.5 5"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                                <span className="d-none d-sm-inline ms-1">{t('PREV') || 'Prev'}</span>
                            </button>
                            <span className="pagination-text">
                                {t('PAGE') || 'Page'} {currentPage} {t('TO') || 'to'} {totalPages}
                            </span>
                            <button
                                className="pagination-btn"
                                onClick={handleNextPage}
                                disabled={currentPage === totalPages}
                            >
                                <span className="d-none d-sm-inline me-1">{t('NEXT') || 'Next'}</span>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path
                                        d="M7.5 5L12.5 10L7.5 15"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                        </div>
                    </Col>

                    {/* Right side - Invoice preview */}
                    <Col md={6} className="preview-column">
                        <div className="preview-header mb-3">
                            <span className="preview-label">{t('PREVIEW') || 'Preview'}</span>
                        </div>
                        <div className="preview-container">
                            <InvoicePreview 
                                className="w-100"
                            />
                        </div>
                    </Col>
                </Row>
            </Modal.Body>

            <Modal.Footer className="border-0 pt-0">
                <Button
                    variant="outline-secondary"
                    onClick={onClose}
                    className="cancel-btn"
                    disabled={isLoadingCopy}
                >
                    {t('CANCEL') || 'Cancel'}
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSelectAndCopy}
                    disabled={!selectedInvoice || isLoadingCopy}
                    className="select-copy-btn"
                >
                    {isLoadingCopy ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            {t('LOADING') || 'Loading...'}
                        </>
                    ) : (
                        <>
                            {t('SELECT_AND_COPY') || 'Select & Copy'}
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="ms-2">
                                <path
                                    d="M5.33333 3.99967L10.6667 3.99967M10.6667 3.99967L10.6667 9.33301M10.6667 3.99967L3.33333 11.333"
                                    stroke="white"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
